"use client";

import * as React from "react";
import { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@focal/ui-kit";
import { Label } from "@focal/ui-kit";
import {
  getAllExcelTemplates,
  fillExcelTemplate,
  fillConversationExcelTemplate,
} from "@/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { LoadingSpinner } from "@focal/ui-kit";
import { ExcelFillRequest } from "@/types";

export interface ExcelTemplateSelectorProps {
  conversationId?: string;
  onSuccess?: (templateName: string) => void;
  onError?: (error: Error) => void;
  className?: string;
}

export const ExcelTemplateSelector: React.FC<ExcelTemplateSelectorProps> = ({
  conversationId,
  onSuccess,
  onError,
  className,
}) => {
  // Fetch all Excel templates
  const {
    data: templates,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useQuery({
    queryKey: ["excelTemplates"],
    queryFn: getAllExcelTemplates,
  });

  // Fill and download mutation
  const fillMutation = useMutation({
    mutationFn: async ({
      templateId,
      templateName,
    }: {
      templateId: string;
      templateName: string;
    }) => {
      // First get the prefilled data from the conversation
      const prefilledData = await fillConversationExcelTemplate(
        conversationId!,
        templateId
      );

      // The API returns fillInstructions with data and rowData nested inside
      const fillData: ExcelFillRequest = {
        data: prefilledData?.fillInstructions?.data || {},
        rowData: prefilledData?.fillInstructions?.rowData || {},
      };

      // Then fill and get the blob
      const blob = await fillExcelTemplate(templateId, fillData);
      return { blob, templateName };
    },
    onSuccess: ({ blob, templateName }) => {
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${templateName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      if (onSuccess) {
        onSuccess(templateName);
      }
    },
    onError: (error: Error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  const handleTemplateSelect = (templateId: string) => {
    if (!templateId || !conversationId) return;

    const template = templates?.find((t) => t.id === templateId);
    const templateName = template?.name || "filled-spreadsheet";

    fillMutation.mutate({
      templateId,
      templateName,
    });
  };

  const isPending = fillMutation.isPending;

  if (templatesError) {
    return (
      <div className="rounded-md border border-error bg-error-foreground p-4">
        <p className="text-error text-sm">
          Error loading templates: {(templatesError as Error).message}
        </p>
      </div>
    );
  }

  // Show message when no templates are available
  if (!isLoadingTemplates && (!templates || templates.length === 0)) {
    return (
      <div className={className}>
        <div className="rounded-md border border-muted bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            No Excel templates available. Please contact{" "}
            <a
              href="mailto:support@meetwithfocal.com"
              className="text-primary underline hover:text-primary/80"
            >
              support@meetwithfocal.com
            </a>{" "}
            to add a template.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-6">
        {/* Template Selection */}
        <div className="space-y-2">
          <Label htmlFor="excel-template-select">Select Excel Template</Label>
          <Select
            onValueChange={handleTemplateSelect}
            disabled={isLoadingTemplates || isPending}
          >
            <SelectTrigger id="excel-template-select">
              <SelectValue placeholder="Choose a template to fill and download..." />
            </SelectTrigger>
            <SelectContent>
              {templates?.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Loading State */}
        {isPending && (
          <div className="flex items-center justify-center py-4">
            <LoadingSpinner className="text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">
              Generating spreadsheet...
            </span>
          </div>
        )}

        {/* Error */}
        {fillMutation.error && (
          <div className="rounded-md border border-error bg-error-foreground p-4">
            <p className="text-error text-sm">
              Error generating spreadsheet: {fillMutation.error.message}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
